import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Input,
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';

/**
 * Reusable tag picker popover with search and checkboxes.
 * Visual-only — delegates toggle logic to the parent via `onToggle`.
 *
 * Props:
 *  - allTags: Array of { id, name, color }
 *  - selectedTagIds: Set or Array of selected tag IDs
 *  - onToggle: (tag) => void — called when a tag is checked/unchecked
 *  - children: trigger element (rendered inside PopoverTrigger)
 *  - placement: popover placement (default "bottom-end")
 */
export default function TagPickerPopover({ allTags, selectedTagIds, onToggle, children, placement = "bottom-end" }) {
  const { t } = useTranslation('movements');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const popoverRef = useRef(null);

  const selectedSet = useMemo(
    () => (selectedTagIds instanceof Set ? selectedTagIds : new Set(selectedTagIds)),
    [selectedTagIds]
  );

  const filteredTags = useMemo(() => {
    const matches = allTags.filter(t => {
      // Hide inactive tags unless already assigned, so users can unassign them but not pick new ones
      if (t.is_active === false && !selectedSet.has(t.id)) return false;
      return !search || t.name.toLowerCase().includes(search.toLowerCase());
    });
    return matches.sort((a, b) => {
      const aActive = a.is_active !== false;
      const bActive = b.is_active !== false;
      if (aActive !== bActive) return aActive ? -1 : 1;
      return 0;
    });
  }, [allTags, search, selectedSet]);

  const close = () => { setIsOpen(false); setSearch(''); };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        e.stopPropagation();
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [isOpen]);

  return (
    <Popover isOpen={isOpen} onClose={close} placement={placement} isLazy>
      <PopoverTrigger>
        <Box onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }} cursor="pointer" display="inline-flex">
          {children}
        </Box>
      </PopoverTrigger>
      <PopoverContent
        ref={popoverRef}
        w="250px"
        boxShadow="0 4px 24px rgba(0,0,0,0.12)"
        borderRadius="12px"
        border="1px solid"
        borderColor="redesign.border"
        zIndex={10}
        onClick={(e) => e.stopPropagation()}
        overflow="hidden"
      >
        <PopoverBody p={0}>
          <Flex align="center" gap={2} px={4} py="8px" borderBottom="1px solid" borderBottomColor="redesign.border">
            <SearchIcon color="redesign.text.muted" boxSize="12px" />
            <Input
              placeholder={t('shared.tagPicker.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="unstyled"
              fontSize="sm"
              h="28px"
              autoFocus
            />
          </Flex>
          <Box maxH="240px" overflowY="auto" py={2}>
            {filteredTags.map(tag => {
              const isChecked = selectedSet.has(tag.id);
              const isInactive = tag.is_active === false;
              return (
                <Flex
                  key={tag.id}
                  align="center"
                  gap={2}
                  px={4}
                  py="7px"
                  cursor="pointer"
                  opacity={isInactive ? 0.5 : 1}
                  _hover={{ bg: "redesign.bg.hover" }}
                  onClick={() => onToggle(tag)}
                >
                  <Checkbox size="sm" isChecked={isChecked} colorScheme="blue" pointerEvents="none" flexShrink={0} />
                  <Box w="7px" h="7px" borderRadius="full" bg={tag.color || "#3182CE"} flexShrink={0} />
                  <Text fontSize="sm" color="redesign.text" noOfLines={1}>{tag.name}</Text>
                </Flex>
              );
            })}
            {filteredTags.length === 0 && (
              <Text px={4} py={3} fontSize="sm" color="redesign.text.muted">{t('shared.tagPicker.noResults')}</Text>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
